import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  orderBy,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../../firebase/config';

const COLLECTION = 'festpreise';

export const fetchFestpreise = createAsyncThunk(
  'festpreise/fetchFestpreise',
  async (_, { rejectWithValue }) => {
    try {
      const q = query(collection(db, COLLECTION), orderBy('createdAt'));
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.()?.toISOString() || null
      }));
    } catch (error) {
      console.error('Fehler beim Laden der Festpreise:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const addFestpreis = createAsyncThunk(
  'festpreise/addFestpreis',
  async (festpreis, { rejectWithValue }) => {
    try {
      const docRef = await addDoc(collection(db, COLLECTION), {
        ...festpreis,
        createdAt: serverTimestamp()
      });
      return { id: docRef.id, ...festpreis, createdAt: new Date().toISOString() };
    } catch (error) {
      console.error('Fehler beim Erstellen des Festpreises:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const updateFestpreis = createAsyncThunk(
  'festpreise/updateFestpreis',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      await updateDoc(doc(db, COLLECTION, id), data);
      return { id, ...data };
    } catch (error) {
      console.error('Fehler beim Aktualisieren des Festpreises:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const deleteFestpreis = createAsyncThunk(
  'festpreise/deleteFestpreis',
  async (id, { rejectWithValue }) => {
    try {
      await deleteDoc(doc(db, COLLECTION, id));
      return id;
    } catch (error) {
      console.error('Fehler beim Löschen des Festpreises:', error);
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  items: [],
  isLoading: false,
  isSaving: false,
  error: null
};

const festpreiseSlice = createSlice({
  name: 'festpreise',
  initialState,
  reducers: {
    clearFestpreiseError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFestpreise.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchFestpreise.fulfilled, (state, action) => {
        state.isLoading = false;
        state.items = action.payload;
      })
      .addCase(fetchFestpreise.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(addFestpreis.pending, (state) => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(addFestpreis.fulfilled, (state, action) => {
        state.isSaving = false;
        state.items.push(action.payload);
      })
      .addCase(addFestpreis.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload;
      })
      .addCase(updateFestpreis.pending, (state) => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(updateFestpreis.fulfilled, (state, action) => {
        state.isSaving = false;
        const index = state.items.findIndex(item => item.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = { ...state.items[index], ...action.payload };
        }
      })
      .addCase(updateFestpreis.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload;
      })
      .addCase(deleteFestpreis.pending, (state) => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(deleteFestpreis.fulfilled, (state, action) => {
        state.isSaving = false;
        state.items = state.items.filter(item => item.id !== action.payload);
      })
      .addCase(deleteFestpreis.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload;
      });
  }
});

export const { clearFestpreiseError } = festpreiseSlice.actions;
export default festpreiseSlice.reducer;
